import { applyAdditiveBone, applyOverrideBone, blendPoseBuffers, ease } from "./math.js";
import { applyRotationLimits, evaluateWorldTransforms } from "./kinematics.js";
import type { AnimatorEvent, CompiledClip, CompiledSkeleton, EasingName, LayerMode, PoseFrame } from "./types.js";

interface Playback {
  clip: number;
  time: number;
  speed: number;
}

interface Snapshot {
  local: Float64Array;
  visibility: Float64Array;
  order: Int32Array;
}

interface Transition {
  source: Playback | Snapshot;
  elapsed: number;
  duration: number;
  easing: EasingName;
}

interface Layer {
  id: string;
  order: number;
  mode: LayerMode;
  mask: number[];
  weight: number;
  playback: Playback | null;
  transition: Transition | null;
}

export interface PlayOptions {
  layer?: string;
  fade?: number;
  easing?: EasingName;
  speed?: number;
  mode?: LayerMode;
  mask?: string | number[];
  weight?: number;
  order?: number;
}

export interface LayerOptions {
  mode?: LayerMode;
  mask?: string | number[];
  weight?: number;
  order?: number;
}

function frameBuffers(definition: CompiledSkeleton): Snapshot {
  return {
    local: new Float64Array(definition.neutral),
    visibility: new Float64Array(definition.neutralSlotVisibility),
    order: new Int32Array(definition.neutralSlotOrder),
  };
}

function copySnapshot(source: Snapshot): Snapshot {
  return { local: new Float64Array(source.local), visibility: new Float64Array(source.visibility), order: new Int32Array(source.order) };
}

function clipTime(clip: CompiledClip, time: number): number {
  if (!clip.loop) return Math.max(0, Math.min(clip.duration, time));
  const wrapped = time % clip.duration;
  return wrapped < 0 ? wrapped + clip.duration : wrapped;
}

function sampleClip(definition: CompiledSkeleton, playback: Playback, out = frameBuffers(definition)): Snapshot {
  const clip = definition.clips[playback.clip];
  const time = clipTime(clip, playback.time);
  let right = clip.keys.findIndex((key) => key.at >= time);
  if (right < 0) right = clip.keys.length - 1;
  if (right === 0 || clip.keys[right].at === time) {
    const pose = definition.poses[clip.keys[right].pose];
    out.local.set(pose.transforms);
    out.visibility.set(pose.slotVisibility);
    out.order.set(pose.slotOrder);
    return out;
  }
  const left = right - 1;
  const fromKey = clip.keys[left];
  const toKey = clip.keys[right];
  const alpha = ease(toKey.easing, (time - fromKey.at) / (toKey.at - fromKey.at));
  const from = definition.poses[fromKey.pose];
  const to = definition.poses[toKey.pose];
  blendPoseBuffers(from.transforms, to.transforms, alpha, out.local);
  for (let index = 0; index < out.visibility.length; index += 1) {
    out.visibility[index] = from.slotVisibility[index] + (to.slotVisibility[index] - from.slotVisibility[index]) * alpha;
    out.order[index] = alpha < 0.5 ? from.slotOrder[index] : to.slotOrder[index];
  }
  return out;
}

function isSnapshot(source: Playback | Snapshot): source is Snapshot {
  return "local" in source;
}

export class Animator {
  private readonly clips: Map<string, number>;
  private readonly layers = new Map<string, Layer>();
  private events: AnimatorEvent[] = [];

  constructor(readonly definition: CompiledSkeleton) {
    this.clips = new Map(definition.clips.map((clip, index) => [clip.id, index]));
    this.layers.set("base", { id: "base", order: 0, mode: "override", mask: definition.bones.map((_, index) => index), weight: 1, playback: null, transition: null });
  }

  configureLayer(id: string, options: LayerOptions = {}): void {
    const layer = this.layer(id, options);
    this.applyLayerOptions(layer, options);
  }

  play(clipId: string, options: PlayOptions = {}): void {
    const clip = this.clips.get(clipId);
    if (clip === undefined) throw new Error(`Unknown skeletal clip: ${clipId}`);
    const layer = this.layer(options.layer ?? "base", options);
    this.applyLayerOptions(layer, options);
    const target: Playback = { clip, time: 0, speed: Math.max(0.0001, Number(options.speed ?? 1)) };
    const definition = this.definition.clips[clip];
    const duration = Math.max(0, Number(options.fade ?? definition.transition.duration));
    if (!layer.playback || duration === 0) {
      layer.playback = target;
      layer.transition = null;
      return;
    }
    const source = layer.transition ? copySnapshot(this.sampleLayer(layer)) : { ...layer.playback };
    layer.playback = target;
    layer.transition = { source, elapsed: 0, duration, easing: options.easing ?? definition.transition.easing };
  }

  stop(layerId = "base"): void {
    const layer = this.layers.get(layerId);
    if (!layer) return;
    layer.playback = null;
    layer.transition = null;
  }

  seek(layerId: string, time: number, emitEvents = false): AnimatorEvent[] {
    const layer = this.layers.get(layerId);
    if (!layer?.playback) return [];
    const previous = layer.playback.time;
    layer.playback.time = Math.max(0, Number(time));
    const events = emitEvents ? this.eventsBetween(layer, layer.playback, previous, layer.playback.time) : [];
    this.events.push(...events);
    return events;
  }

  update(deltaSeconds: number): AnimatorEvent[] {
    const delta = Math.max(0, Number(deltaSeconds) || 0);
    this.events = [];
    for (const layer of this.layers.values()) {
      if (!layer.playback) continue;
      const previous = layer.playback.time;
      layer.playback.time += delta * layer.playback.speed;
      this.events.push(...this.eventsBetween(layer, layer.playback, previous, layer.playback.time));
      if (layer.transition) {
        layer.transition.elapsed += delta;
        if (!isSnapshot(layer.transition.source)) layer.transition.source.time += delta * layer.transition.source.speed;
        if (layer.transition.elapsed >= layer.transition.duration) layer.transition = null;
      }
    }
    return [...this.events];
  }

  evaluate(): PoseFrame {
    const result = frameBuffers(this.definition);
    const orderedLayers = [...this.layers.values()].filter((layer) => layer.playback).sort((left, right) => left.order - right.order);
    for (const layer of orderedLayers) {
      const sampled = this.sampleLayer(layer);
      const weight = Math.max(0, Math.min(1, layer.weight));
      const masked = new Set(layer.mask);
      for (const bone of layer.mask) {
        if (layer.mode === "additive") applyAdditiveBone(result.local, sampled.local, this.definition.neutral, bone, weight);
        else applyOverrideBone(result.local, sampled.local, bone, weight);
      }
      if (layer.mode === "override") {
        for (let slot = 0; slot < this.definition.slots.length; slot += 1) {
          if (!masked.has(this.definition.slots[slot].bone)) continue;
          result.visibility[slot] += (sampled.visibility[slot] - result.visibility[slot]) * weight;
          if (weight >= 0.5) result.order[slot] = sampled.order[slot];
        }
      }
    }
    applyRotationLimits(this.definition, result.local);
    const world = evaluateWorldTransforms(this.definition, result.local);
    return { local: result.local, world, slotVisibility: result.visibility, slotOrder: result.order };
  }

  private layer(id: string, options: LayerOptions): Layer {
    const existing = this.layers.get(id);
    if (existing) return existing;
    const layer: Layer = {
      id, order: Number(options.order ?? this.layers.size), mode: options.mode ?? "override", mask: this.resolveMask(options.mask), weight: Number(options.weight ?? 1), playback: null, transition: null,
    };
    this.layers.set(id, layer);
    return layer;
  }

  private applyLayerOptions(layer: Layer, options: LayerOptions): void {
    if (options.mode) layer.mode = options.mode;
    if (options.mask !== undefined) layer.mask = this.resolveMask(options.mask);
    if (options.weight !== undefined) layer.weight = Number(options.weight);
    if (options.order !== undefined) layer.order = Number(options.order);
  }

  private resolveMask(mask: string | number[] | undefined): number[] {
    if (mask === undefined) return this.definition.bones.map((_, index) => index);
    if (typeof mask === "string") {
      const resolved = this.definition.masks[mask];
      if (!resolved) throw new Error(`Unknown skeletal bone mask: ${mask}`);
      return [...resolved];
    }
    return [...mask];
  }

  private sampleLayer(layer: Layer): Snapshot {
    if (!layer.playback) return frameBuffers(this.definition);
    const target = sampleClip(this.definition, layer.playback);
    if (!layer.transition) return target;
    const source = isSnapshot(layer.transition.source) ? layer.transition.source : sampleClip(this.definition, layer.transition.source);
    const alpha = ease(layer.transition.easing, Math.min(1, layer.transition.elapsed / layer.transition.duration));
    const out = frameBuffers(this.definition);
    blendPoseBuffers(source.local, target.local, alpha, out.local);
    for (let index = 0; index < out.visibility.length; index += 1) {
      out.visibility[index] = source.visibility[index] + (target.visibility[index] - source.visibility[index]) * alpha;
      out.order[index] = alpha < 0.5 ? source.order[index] : target.order[index];
    }
    return out;
  }

  private eventsBetween(layer: Layer, playback: Playback, previous: number, next: number): AnimatorEvent[] {
    const clip = this.definition.clips[playback.clip];
    if (next <= previous) return [];
    const output: AnimatorEvent[] = [];
    const startCycle = clip.loop ? Math.floor(previous / clip.duration) : 0;
    const endCycle = clip.loop ? Math.floor(next / clip.duration) : 0;
    for (let cycle = startCycle; cycle <= endCycle; cycle += 1) {
      for (const event of clip.events) {
        const at = event.at + cycle * clip.duration;
        if (at > previous && at <= next && (clip.loop || at <= clip.duration)) output.push({ layer: layer.id, clip: clip.id, name: event.name, data: event.data });
      }
    }
    return output;
  }
}

export function sampleClipPose(definition: CompiledSkeleton, clipId: string, time: number): PoseFrame {
  const index = definition.clips.findIndex((clip) => clip.id === clipId);
  if (index < 0) throw new Error(`Unknown skeletal clip: ${clipId}`);
  const sampled = sampleClip(definition, { clip: index, time, speed: 1 });
  applyRotationLimits(definition, sampled.local);
  return { local: sampled.local, world: evaluateWorldTransforms(definition, sampled.local), slotVisibility: sampled.visibility, slotOrder: sampled.order };
}
